import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  logInfo,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const DISCOUNT_KIT_SYNC_START_EVENT = 'discount-kit:sync:start';
const DISCOUNT_KIT_SYNC_END_EVENT = 'discount-kit:sync:end';
const DISCOUNT_KIT_SYNC_ROOT_ATTRIBUTE = 'data-discount-kit-syncing';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement | HTMLElement[]} [cartSyncStatus] - The Discount Kit sync status elements.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(DISCOUNT_KIT_SYNC_START_EVENT, this.#handleDiscountKitSyncStart);
    document.addEventListener(DISCOUNT_KIT_SYNC_END_EVENT, this.#handleDiscountKitSyncEnd);

    this.setAttribute('aria-busy', 'false');
    this.#syncDiscountKitStateFromDocument();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(DISCOUNT_KIT_SYNC_START_EVENT, this.#handleDiscountKitSyncStart);
    document.removeEventListener(DISCOUNT_KIT_SYNC_END_EVENT, this.#handleDiscountKitSyncEnd);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleDiscountKitSyncStart = () => {
    this.dataset.cartSyncing = 'true';
    this.setAttribute('aria-busy', 'true');
    this.#setCartSyncStatusText(true);
  };

  #handleDiscountKitSyncEnd = () => {
    this.removeAttribute('data-cart-syncing');
    this.setAttribute('aria-busy', 'false');
    this.#setCartSyncStatusText(false);
  };

  #syncDiscountKitStateFromDocument = () => {
    if (document.documentElement.hasAttribute(DISCOUNT_KIT_SYNC_ROOT_ATTRIBUTE)) {
      this.#handleDiscountKitSyncStart();
      return;
    }

    this.#handleDiscountKitSyncEnd();
  };

  /**
   * Updates all Discount Kit sync status elements inside this component.
   * @param {boolean} isSyncing
   */
  #setCartSyncStatusText(isSyncing) {
    const cartSyncStatuses = Array.isArray(this.refs.cartSyncStatus)
      ? this.refs.cartSyncStatus
      : [this.refs.cartSyncStatus];

    for (const cartSyncStatus of cartSyncStatuses) {
      if (!(cartSyncStatus instanceof HTMLElement)) continue;
      cartSyncStatus.textContent = isSyncing ? cartSyncStatus.dataset.message || '' : '';
    }
  }

  /**
   * Re-renders every mounted cart section from the server, discarding the section
   * cache.
   *
   * Every cart-items-component on the page renders the same cart, so when that render
   * goes stale it goes stale everywhere — not just in the component that happened to
   * notice. On /cart that means two instances, the main cart section and the header
   * drawer, and refreshing only one would leave the other showing what we already know
   * is out of date. `updateQuantity` collects section ids the same way for the same
   * reason.
   *
   * `cache: false` is load-bearing: the renderer snapshots every section on page load
   * and would otherwise hand back the very markup we are trying to replace.
   */
  #rerenderCartSections() {
    return Promise.allSettled(
      Array.from(this.#collectCartSectionIds(), (sectionId) => sectionRenderer.renderSection(sectionId, { cache: false }))
    );
  }

  /**
   * Public entry point for a server refresh triggered from outside this component —
   * currently the back-from-checkout recovery in `assets/cart-drawer.js`.
   *
   * Since `#rerenderCartSections` already fans out across every mounted cart section,
   * calling this on any single instance refreshes them all and the caller does not have
   * to work out which one to reach for.
   *
   * Deliberately separate from `debugRerenderSections`, which shares the same underlying
   * refresh but exists to be deleted with the debug toolbar.
   *
   * @returns {Promise<PromiseSettledResult<unknown>[]>}
   */
  refreshFromServer() {
    return this.#rerenderCartSections();
  }

  /**
   * Section IDs of every mounted cart-items-component, including this one.
   * @returns {Set<string>}
   */
  #collectCartSectionIds() {
    const sectionIds = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return sectionIds;
  }

  /**
   * DEBUG — bound to the "Re-render sections" button in snippets/cart-products.liquid
   * (see the `cart_debug_buttons` flag there). The narrower of the two refreshes: a GET
   * against the Section Rendering API per cart section, morphed in. Markup only — no
   * cart JSON is fetched and no `cart:update` is fired, so nothing that reacts to that
   * event runs.
   *
   * Remove alongside the toolbar markup.
   */
  async debugRerenderSections() {
    const startedAt = performance.now();
    const sectionIds = Array.from(this.#collectCartSectionIds());

    this.#setDebugStatus(`re-rendering ${sectionIds.length} section(s)…`);
    logInfo('[cart-debug] renderSection', sectionIds);

    const results = await this.#rerenderCartSections();
    const failed = results.filter((result) => result.status === 'rejected');

    for (const failure of failed) console.warn('[cart-debug] renderSection failed', failure.reason);

    // Written after the morph on purpose: the fresh server HTML has an empty status,
    // so anything set beforehand is overwritten by the very render we are reporting on.
    this.#setDebugStatus(
      `re-rendered ${sectionIds.length - failed.length}/${sectionIds.length} in ${Math.round(performance.now() - startedAt)}ms`
    );
  }

  /**
   * DEBUG — bound to the "Force cart update" button. The wider refresh: a no-op write
   * to the cart endpoint (`updates: {}`) that returns cart JSON *and* rendered section
   * HTML in one round trip, then a `cart:update` so every listener reacts with real
   * cart data — not just the components that happen to be re-rendered.
   *
   * Deliberately a `CartUpdateEvent` and not a `CartAddEvent`: the drawer's auto-open
   * only fires for the latter (`assets/cart-drawer.js`), and a refresh should not pop
   * the drawer open.
   *
   * Remove alongside the toolbar markup.
   */
  async debugForceCartUpdate() {
    const startedAt = performance.now();
    const sectionIds = Array.from(this.#collectCartSectionIds());

    this.#setDebugStatus('posting cart update…');

    const body = JSON.stringify({
      updates: {},
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    logInfo('[cart-debug] POST', Theme.routes.cart_update_url, JSON.parse(body));

    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        credentials: 'same-origin',
      });

      if (!response.ok) {
        console.warn('[cart-debug] cart update failed', response.status, await response.text());
        this.#setDebugStatus(`cart update failed — HTTP ${response.status}`);
        return;
      }

      const cart = await response.json();
      logInfo('[cart-debug] cart', cart);

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.sectionId, {
          itemCount: cart.item_count,
          source: 'cart-debug-force-update',
          sections: cart.sections,
        })
      );

      // Siblings morph from the event above, but #handleCartUpdate skips the
      // dispatching component (`event.target === this`), so this one morphs itself —
      // the same split updateQuantity uses.
      const ownSectionHTML = cart.sections?.[this.sectionId];
      if (ownSectionHTML) {
        morphSection(this.sectionId, ownSectionHTML, this.isDrawer ? 'hydration' : 'full');
      }

      this.#setDebugStatus(
        `cart updated — ${cart.item_count} item(s) in ${Math.round(performance.now() - startedAt)}ms`
      );
    } catch (error) {
      console.warn('[cart-debug] cart update threw', error);
      this.#setDebugStatus('cart update threw — see console');
    }
  }

  /**
   * DEBUG — writes to the readout beside the toolbar buttons.
   *
   * Queries at write time rather than going through `this.refs`, because a morph may
   * have replaced the element since refs were collected.
   *
   * @param {string} message
   */
  #setDebugStatus(message) {
    for (const element of document.querySelectorAll('[data-cart-debug-status]')) {
      element.textContent = message;
    }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;
    this.dispatchEvent(new CartErrorEvent(this.sectionId, parsedResponseText.errors, parsedResponseText.errors, parsedResponseText.errors));
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      // Load-bearing fallback. Still active for DiscountUpdateEvent (cart-discount.js)
      // and any future caller that dispatches CartUpdateEvent without a sections payload.
      // Do not remove without auditing every CartUpdateEvent dispatcher in the codebase.
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
